import type { MapRef } from 'react-map-gl/mapbox'

import { Alert } from '@/components/ui'

import { useTrackerMapContext } from './TrackerMapContext'
import { TrackerTable } from './TrackerTable'
import { TrackerShipmentRow } from './types'
import { rowId } from './utils'

interface TrackerPanelProps {
  mapRef: React.RefObject<MapRef | null>
  setCurrentRowId: (id: number | undefined) => void
  onHeaderMeasured?: (rect: { top: number; height: number; right: number }) => void
}

export const TrackerPanel = ({ mapRef, setCurrentRowId, onHeaderMeasured }: TrackerPanelProps) => {
  const context = useTrackerMapContext()

  const trackers = context.sidebarState.limitToMap ? context.viewportFilteredTrackers : context.filteredTrackers

  const showSkeleton = context.isLoading && context.allTrackers.length === 0 && context.companyId

  const handleClick = (row: TrackerShipmentRow) => {
    setCurrentRowId(rowId(row))
    const map = mapRef.current?.getMap()
    if (map && row.location) {
      map.flyTo({ center: [row.location.longitude, row.location.latitude], duration: 1000 })
    }
    // With no map to click again, a row click is the way into the details, as on the asset map.
    if (context.sidebarState.layoutMode === 'table') context.setDetailSheetIsOpen(true)
  }

  if (showSkeleton) {
    return (
      <div className="flex flex-col min-h-0 w-full h-full overflow-hidden">
        <div className="bg-primary h-10 shrink-0" />
        <div className="flex-1 overflow-hidden flex flex-col">
          {Array.from({ length: 15 }).map((_, i) => (
            <div
              key={i}
              className={`h-[35px] shrink-0 flex items-center gap-3 px-4 border-b border-base-200 ${i % 2 === 0 ? 'bg-base-100' : 'bg-base-200'}`}
            >
              <div className="skeleton h-4 w-16" />
              <div className="skeleton h-4 flex-1 max-w-48" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (!context.companyId && context.allTrackers.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        <span className="text-sm text-base-content/70 text-center">Choose a company to load trackers.</span>
      </div>
    )
  }

  if (context.allTrackers.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        {context.error ? (
          <Alert message={context.error} type="info" />
        ) : (
          <span className="text-sm text-base-content/70">No trackers found.</span>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col min-h-0 w-full h-full overflow-hidden">
      {context.error && !context.isLoading && <Alert message={context.error} type="error" />}

      <TrackerTable
        trackers={trackers}
        currentRowId={context.currentRowId}
        onTrackerClick={handleClick}
        pinnedOnly={context.detailSheetIsOpen && context.sidebarState.layoutMode !== 'split'}
        onHeaderMeasured={onHeaderMeasured}
      />
    </div>
  )
}
